import React, { useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  HStack,
  Icon,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { Check, CircleDot } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { WorkingModeActivity } from '@/common/api/models/workingModeActivity';
import type { Task } from '@/common/api/models/task';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import {
  buildApplyChecklist,
  MODE_LABEL_KEYS,
  resolveTenantDefaultMinLevelsSummary,
  type ApplyChecklistItem,
  type WorkingModeWire,
} from './workingModeDisplay';

interface WorkingModeApplyDialogProps {
  isOpen: boolean;
  targetMode: WorkingModeWire;
  activities: WorkingModeActivity[];
  tasks?: Task[];
  isApplying: boolean;
  applyError?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const ChecklistRow: React.FC<{ item: ApplyChecklistItem }> = ({ item }) => {
  const { t } = useTranslation();
  const isConfirm = item.tone === 'confirm';
  return (
    <HStack space="sm" alignItems="flex-start">
      <Icon
        as={isConfirm ? Check : CircleDot}
        size="xs"
        color={isConfirm ? '$statusSuccess' : '$textSubtle'}
        mt="$0.5"
      />
      <Text
        size="sm"
        color={isConfirm ? '$textSubtle' : '$textBody'}
        flex={1}
      >
        {t(item.messageKey, item.messageParams)}
      </Text>
    </HStack>
  );
};

const WorkingModeApplyDialog: React.FC<WorkingModeApplyDialogProps> = ({
  isOpen,
  targetMode,
  activities,
  tasks,
  isApplying,
  applyError,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { editableDimensionKeys, evalDimensionKeys, performanceLevels } = useTenantGradingSettings();
  const minLevelsSummary = useMemo(
    () => resolveTenantDefaultMinLevelsSummary({
      editableDimensionKeys,
      allDimensionKeys: evalDimensionKeys,
      performanceLevels,
    }),
    [editableDimensionKeys, evalDimensionKeys, performanceLevels],
  );
  const checklist = useMemo(
    () => buildApplyChecklist(activities, tasks, { minLevelsSummary, targetMode }),
    [activities, tasks, minLevelsSummary, targetMode],
  );

  if (!isOpen) return null;

  const hasEssayActions = checklist.sections.some(
    (section) => section.id === 'essays' && section.items.some((item) => item.tone === 'action'),
  );

  return (
    <AlertDialog isOpen={isOpen} onClose={onCancel}>
      <AlertDialogBackdrop />
      <AlertDialogContent softShadow="3" testID="working-mode-apply-dialog">
        <AlertDialogHeader borderBottomWidth={1} borderBottomColor="$borderLight">
          <VStack space="xs">
            <Text size="lg" fontWeight="semibold" color="$textHeading">
              {t('lecturer.moduleEditor.workingMode.dialog.title')}
            </Text>
            <Text size="sm" color="$textSubtle">
              {t('lecturer.moduleEditor.workingMode.dialog.subtitle', {
                mode: t(MODE_LABEL_KEYS[targetMode]),
              })}
            </Text>
          </VStack>
        </AlertDialogHeader>
        <AlertDialogBody>
          <VStack space="lg" py="$2">
            {checklist.sections.map((section) => (
              <VStack key={section.id} space="sm" testID={`working-mode-phase-${section.id}`}>
                <Text size="sm" fontWeight="semibold" color="$textHeading">
                  {t(section.titleKey)}
                </Text>
                <VStack space="sm">
                  {section.items.map((item) => (
                    <ChecklistRow key={item.id} item={item} />
                  ))}
                </VStack>
              </VStack>
            ))}
            {hasEssayActions && (
              <Text size="xs" color="$textHint">
                {t('lecturer.moduleEditor.workingMode.dialog.essayFootnote')}
              </Text>
            )}
            {applyError && (
              <Text size="sm" color="$statusError" testID="working-mode-apply-error">
                {applyError}
              </Text>
            )}
          </VStack>
        </AlertDialogBody>
        <AlertDialogFooter borderTopWidth={1} borderTopColor="$borderLight">
          <HStack justifyContent="space-between" alignItems="center" w="$full">
            <Text size="xs" color="$textSubtle" flex={1} pr="$3">
              {t('lecturer.moduleEditor.workingMode.dialog.footerSummary')}
            </Text>
            <HStack space="sm">
              <Button variant="outline" onPress={onCancel} isDisabled={isApplying}>
                <ButtonText>{t('common.buttons.cancel')}</ButtonText>
              </Button>
              <Button
                testID="working-mode-apply-confirm"
                onPress={onConfirm}
                isDisabled={isApplying}
              >
                <ButtonText>
                  {t('lecturer.moduleEditor.workingMode.dialog.applyChanges', {
                    count: checklist.changeCount,
                  })}
                </ButtonText>
              </Button>
            </HStack>
          </HStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default WorkingModeApplyDialog;
